<template>
    <ModalContent stitch>
        <template slot="title">{{ title }}</template>
        <template slot="content">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
                {{ $translate("modalIntro") }}
            </p>

            <v-alert
                v-if="isSystemReadonly"
                type="info"
                dense
                outlined
                class="mb-4"
            >
                {{ $translate("systemFormatReadonly") }}
            </v-alert>
            <v-alert
                v-else-if="isSystem"
                type="info"
                dense
                outlined
                class="mb-4"
            >
                {{ $translate("systemFormatEditableByAdmin") }}
            </v-alert>

            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="form.label_singular"
                        :label="$translate('fieldLabelSingular')"
                        counter="128"
                        maxlength="128"
                        required
                        autofocus
                        :disabled="isSystemReadonly"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-text-field
                        v-model="form.label_plural"
                        :label="$translate('fieldLabelPlural')"
                        counter="128"
                        maxlength="128"
                        required
                        :disabled="isSystemReadonly"
                     outlined  dense />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" sm="6">
                    <v-text-field
                        :value="form.content_quantity"
                        @input="
                            form.content_quantity =
                                $utils.normalizeDecimalString($event)
                        "
                        :label="$translate('fieldContentQuantity')"
                        type="text"
                        inputmode="decimal"


                        :disabled="isSystemReadonly"
                     outlined  dense ><template #label><span>{{ $translate('fieldContentQuantity') }}</span><StitchHint :hint="$translate('fieldContentQuantityHint')" /></template></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                    <v-select
                        v-model="form.content_unit"
                        :items="unitChoices"
                        :label="$translate('fieldContentUnit')"


                        :disabled="isSystemReadonly"
                     outlined  dense ><template #label><span>{{ $translate('fieldContentUnit') }}</span><StitchHint :hint="$translate('fieldContentUnitHint')" /></template></v-select>
                </v-col>
            </v-row>
        </template>
        <template slot="bottom">
            <v-btn
                class="my-2 my-sm-4 w-100 w-sm-auto"
                text
                @click="cancelClicked"
            >
                {{ $translate("cancel") }}
            </v-btn>
            <v-btn
                v-if="!isSystemReadonly"
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                color="primary"
                :loading="saving"
                :disabled="!canSave"
                @click="saveClicked"
            >
                {{ $translate("save") }}
            </v-btn>
        </template>
    </ModalContent>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

const UNIT_CHOICES = [
    { value: "cl", text: "cl" },
    { value: "ml", text: "ml" },
    { value: "L", text: "L" },
    { value: "g", text: "g" },
    { value: "kg", text: "kg" },
    { value: "pc", text: "pc" },
];

function localizedDisplayValue(value) {
    if (!value) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    return (value.fr || value.en || "").trim();
}

function localizedPayload(value) {
    const text = (value || "").trim();
    return { fr: text, en: text };
}

export default {
    name: "FormatModal",
    mixins: [GlobalMixins],
    props: {
        params: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        const editing = this.params?.format || this.params?.saleformat || null;
        return {
            saving: false,
            unitChoices: UNIT_CHOICES,
            form: editing
                ? {
                      label_singular: localizedDisplayValue(
                          editing.label_singular,
                      ),
                      label_plural: localizedDisplayValue(editing.label_plural),
                      content_quantity: this.$utils.formatDecimalInputValue(
                          editing.content_quantity,
                          3,
                      ),
                      content_unit: editing.content_unit,
                  }
                : {
                      label_singular: "",
                      label_plural: "",
                      content_quantity: "1",
                      content_unit: "cl",
                  },
        };
    },
    computed: {
        editingItem() {
            return this.params?.format || this.params?.saleformat || null;
        },
        isSystem() {
            return !!this.editingItem?.is_system;
        },
        isSystemReadonly() {
            return this.isSystem && !this.current_user_superuser;
        },
        title() {
            if (this.isSystemReadonly) {
                return this.$translate("viewFormat");
            }
            return this.editingItem
                ? this.$translate("editFormat")
                : this.$translate("addFormat");
        },
        canSave() {
            return !!(
                (this.form.label_singular || "").trim()
                && (this.form.label_plural || "").trim()
            );
        },
    },
    methods: {
        cancelClicked() {
            this.hideModal();
        },
        async saveClicked() {
            if (this.isSystemReadonly) {
                return;
            }
            this.saving = true;
            const payload = {
                label_singular: localizedPayload(this.form.label_singular),
                label_plural: localizedPayload(this.form.label_plural),
                content_quantity: this.form.content_quantity || "1",
                content_unit: this.form.content_unit,
            };
            try {
                let response;
                if (this.editingItem) {
                    response = await axios.patch(
                        `/api/formats/${this.editingItem.id}/`,
                        payload,
                    );
                    this.addAlert({
                        text: this.$translate("saved"),
                        type: "success",
                    });
                } else {
                    response = await axios.post("/api/formats/", payload);
                    this.addAlert({
                        text: this.$translate("created"),
                        type: "success",
                    });
                }
                this.hideModal({ result: response.data });
            } catch (error) {
                console.error("Error saving format:", error);
                const details = error.details || error.response?.data;
                const message =
                    details?.label_singular?.fr?.[0]
                    || details?.label_singular?.en?.[0]
                    || details?.label_singular?.[0]
                    || details?.non_field_errors?.[0]
                    || this.$translate("errorSaving");
                this.addAlert({ text: message, type: "error" });
            } finally {
                this.saving = false;
            }
        },
    },
    i18n: {
        messages: {
            en: {
                modalIntro:
                    "Reusable unit formats. Labels are the container only (e.g. Glass, Bottle); capacity comes from quantity and unit.",
                addFormat: "Add format",
                editFormat: "Edit format",
                viewFormat: "Format",
                systemFormatReadonly:
                    "This is a Safetee system format and cannot be edited.",
                systemFormatEditableByAdmin:
                    "This is a Safetee system format. Changes apply globally for all entities.",
                fieldLabelSingular: "Container (singular)",
                fieldLabelPlural: "Container (plural)",
                fieldContentQuantity: "Capacity",
                fieldContentQuantityHint:
                    "Amount per unit (e.g. 15 for a 15 cl glass). Shown next to the container label.",
                fieldContentUnit: "Unit",
                fieldContentUnitHint: "Weight or volume unit of the capacity.",
                cancel: "Cancel",
                save: "Save",
                created: "Format created",
                saved: "Format saved",
                errorSaving: "Failed to save format",
            },
            fr: {
                modalIntro:
                    "Formats réutilisables. Les libellés sont le contenant seul (ex. Verre, Bouteille) ; la contenance vient de la quantité et de l'unité.",
                addFormat: "Ajouter un format",
                editFormat: "Modifier le format",
                viewFormat: "Format",
                systemFormatReadonly:
                    "Ce format système Safetee ne peut pas être modifié.",
                systemFormatEditableByAdmin:
                    "Ceci est un format système Safetee. Les modifications s'appliquent globalement à toutes les entités.",
                fieldLabelSingular: "Contenant (singulier)",
                fieldLabelPlural: "Contenant (pluriel)",
                fieldContentQuantity: "Contenance",
                fieldContentQuantityHint:
                    "Quantité par unité (ex. 15 pour un verre de 15 cl). Affichée avec le contenant.",
                fieldContentUnit: "Unité",
                fieldContentUnitHint:
                    "Unité de poids ou de volume de la contenance.",
                cancel: "Annuler",
                save: "Enregistrer",
                created: "Format créé",
                saved: "Format enregistré",
                errorSaving: "Échec de l'enregistrement du format",
            },
        },
    },
};
</script>
